/**
 * If `value` is an array, return it unchanged. Otherwise, return an
 * array with `value` as only element.
 *
 * @param {unknown} value -
 * @return {Array}
 */
export function asArray(value) {
	if (Array.isArray(value)) {
		return value;
	}
	return [value];
}

/**
 * Return a new array from `array`, filled with as many occurrences of
 * `value` at its beginning to reach a `numElements` length.
 *
 * @param {Array} array -
 * @param {number} numElements - Must be greater than array.length
 * @param {unknown} value - The element that will be copied several times at
 *   the beginning of the returned array
 * @return {Array}
 */
export function padArray(array, numElements, value) {
	return new Array(numElements - array.length).fill(value).concat(array);
}

/**
 * Return the last element of `array` matching a predicate, or
 * undefined if no element matches or `array` is empty.
 *
 * @param {Array} array -
 * @param {function(): boolean} predicate -
 * @return {unknown|undefined}
 */
export function findLast(array, predicate) {
	for (let i = array.length - 1; i >= 0; i--) {
		const element = array[i];
		if (predicate(element)) {
			return element;
		}
	}

	return undefined;
}

/**
 * Return a new array containing the last elements of `array`, in the
 * same order. The length of the returned array is defined by
 * `numElements`.
 *
 * @param {object} options -
 * @param {Array} options.array - The elements from which to take the last
 *   elements to be returned
 * @param {number} options.numElements - A positive integer representing the
 *   size of the result. The function will take that many elements from
 *   `array`
 * @param {number} [options.indexOfLastElement] - A zero-based index
 * indicating which elements at the end of `array` to ignore. If not
 * provided, all elements of `array` are considered
 * @return {Array}
 */
export function lastElementsOfArray({
	array,
	numElements,
	indexOfLastElement = array.length - 1,
}) {
	const startIndex = Math.max(indexOfLastElement - numElements + 1, 0);
	const endIndex = indexOfLastElement + 1;

	return array.slice(startIndex, endIndex);
}

/**
 * Remove the first occurrence of `element` from `array`.
 * Return `array`.
 *
 * @param {Array} array -
 * @param {unknown} element -
 * @return {Array}
 */
export function remove(array, element) {
	const index = array.indexOf(element);
	if (index !== -1) {
		array.splice(index, 1);
	}
	return array;
}

/**
 * Return an array of numbers from `start` (included) to `stop` (not
 * included).
 *
 * @param {number} start - The first value of the returned array
 * @param {number} stop - The value below which numbers should be
 *   included. This value is not in the returned array
 * @param {number} [step] - The distance between a number and the next
 *   number in the array
 * @return {Array}
 */
export function range(start, stop, step = 1) {
	return Array.from(
		{ length: Math.max(Math.ceil((stop - start) / step), 0) },
		(_, i) => i * step + start,
	);
}

/**
 * Return the element of `array` whose value for `selector(element)`
 * is highest among all the elements.  If `array` is empty, return
 * `-Infinity`.
 *
 * @see {@link maxValue} to get the maximum value itself instead of the
 * element with the maximum value.
 * @param {Array} array - Items to compare
 * @param {function(unknown):number} selector - Function to retrieve the
 *   numerical value to compare elements
 * @return {unknown}
 */
export function max(array, selector = (item) => item) {
	if (!array.length) {
		return -Infinity;
	}

	// eslint-disable-next-line no-shadow -- bad practice, we should fix this
	return array.reduce((max, item) =>
		selector(max) > selector(item) ? max : item,
	);
}

/**
 * Return the max value computed
 * using the result of `selector(element)` across all elements of `array`.
 * If `array` is empty, `-Infinity` is returned.
 *
 * @see {@link max} to get the element holding the maximum value instead of the
 *   maximum value itself
 * @param {object[]} array - Items to compare
 * @param {function(object):number} selector - Function to retrieve the
 *   numerical value to use
 * @return {number}
 */
export function maxValue(array, selector = (item) => item) {
	if (!array.length) {
		return -Infinity;
	}

	// eslint-disable-next-line no-shadow -- bad practice, we should fix this
	return array.reduce((max, item) => {
		const transformedItem = selector(item);
		return max > transformedItem ? max : transformedItem;
	}, -Infinity);
}

/**
 * Return a copy of `array` without duplicate elements.
 *
 * If `selector` is provided, it will be passed every element of
 * `array` one by one and the return values will be compared against
 * each other instead of comparing elements of `array` directly.
 *
 * @param {Array} array - Source array
 * @param {function(unknown): unknown} [selector] - Optional element selector function
 * @return {Array}
 */
export function unique(array, selector) {
	const cache = {};
	return array.filter((element) => {
		const key = selector ? selector(element) : element;

		if (key in cache) {
			return false;
		}

		cache[key] = true;
		return cache[key];
	});
}

/**
 * Return an array with the same content as `array` but whose
 * elements are split in groups of size `num`. If `num` is not a
 * divisor of `array`'s length, fill the last group with `padding`.
 *
 * @param {object} config -
 * @param {Array} config.array - Elements to be grouped in groups of size
 * `num`
 * @param {number} config.groupSize - Size of groups
 * @param {object} config.padding - The object to put at the end of the last
 * group when there is not enough elements in array to fill it
 * @return {Array}
 */
export function inGroupsOf({ array = [], groupSize, padding }) {
	return array.reduce((result, element, index) => {
		let currentGroup = result.at(-1);
		const indexWithinGroup = index % groupSize;

		if (indexWithinGroup === 0) {
			// start new group:
			currentGroup = new Array(groupSize).fill(padding);
			result.push(currentGroup);
		}

		currentGroup[indexWithinGroup] = element;

		return result;
	}, []);
}

/**
 * Modify `array` by deleting an element from it and moving the
 * following elements 1 cell to left.
 *
 * @param {Array} array - A non-empty array of anything
 * @param {number} index - The zero-based position of the element to
 * remove from the array
 */
export function removeAt(array, index) {
	array.splice(index, 1);
}

/**
 * Return an array with the same content as `array` but with
 * `separator` as new elements between each of the original array
 * elements.
 *
 * @param {object} config -
 * @param {Array} config.array - An array of anything
 * @param {object} config.separator - New element that should be put
 * between each element `array`
 * @return {Array}
 */
export function insertBetween({ array, separator }) {
	return array.flatMap((x) => [separator, x]).slice(1);
}

/**
 * Return an array of the given `length`, filling it by calling `fn`
 * on each index of the new array.
 *
 * @param {object} config -
 * @param {number} config.length - The number of elements in the resulting
 *   array
 * @param {function(number): unknown} [config.fn] - Takes a number as argument and
 *   should return the value that should be written in the array at
 *   this index
 * @return {Array}
 */
export function fill({ length, fn = () => undefined }) {
	const result = new Array(length);

	for (let i = 0; i < length; ++i) {
		result[i] = fn(i);
	}

	return result;
}

/**
 * Group the elements of `array` by a key returned by `getKey`. The
 * function `getKey` is called with each element of `array` and its
 * return value is used as the key within the returned map where the
 * corresponding element is stored.
 *
 * @see {@link groupBy} for a function returning an array instead
 * @param {object} options -
 * @param {Array} options.array -
 * @param {function(unknown): unknown} options.getKey -
 * @return {Map.<Array>} The keys of this map are the values returned
 *   by `getKey`
 */
export function groupToMap({ array, getKey }) {
	const groups = new Map();

	for (const element of array) {
		const key = getKey(element);
		let group = groups.get(key);

		if (!group) {
			group = [];
			groups.set(key, group);
		}

		group.push(element);
	}

	return groups;
}

/**
 * Return an array of groups of items in `array`. Each group is a
 * {key, value} object where `key` is the result of calling `getKey`
 * on each item of `array` and `value` are the items with that key.
 *
 * @see {@link groupToMap} for a function returning a map instead
 * @param {object} config -
 * @param {Array} config.array - The items to group
 * @param {function(unknown): unknown} config.getKey - This function is called with each item
 *   as argument and must return an object that is used to distinguish
 *   different groups
 * @return {Array<{key,value}>}
 */
export function groupBy({ array, getKey }) {
	const map = groupToMap({ array, getKey });
	const result = [];

	for (const [key, value] of map) {
		result.push({ key, value });
	}

	return result;
}

/**
 * Return an array containing couples of elements from both `array1`
 * and `array2`. I.e., the i-th element of the returned array
 * contains the i-th elements from both input arrays.
 *
 * The length of the result is always the length of `array1`. If
 * `array1` is longer than `array2`, this function will associate
 * extra values of `array1` with `undefined`.  If `array1` is shorter
 * than `array2`, extra elements in `array2` are ignored.
 *
 * @param {Array} array1 - The first array to zip
 * @param {Array} array2 - The second array to zip
 * @return {Array} - The array containing zipped elements
 */
export function zip(array1, array2) {
	return array1.map((array1Item, index) => [array1Item, array2[index]]);
}
